import { useAntdTable, useDebounceFn } from 'ahooks'
import {
  Col,
  DatePicker,
  Form,
  Input,
  Progress,
  Row,
  Select,
  Table,
  TableColumnsType,
  TableProps,
  Typography,
} from 'antd'
import { round, toNumber } from 'lodash'
import { forIn, isArray } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { MosaicComponent, RiskBar, UserAvatar, UsersSearch, getPointPermission } from '@/components'
import { classificationOptions } from '@/modules/portfolio/portfolio-create/portfolio-form.component'
import { routes } from '@/router'
import { PAGE_RESOURCE_POINTS_ENUM, Portfolio, PriorityEnum, ProjectClassification } from '@/schemas'
import { traceEvent } from '@/utils/ga'

import { HotResource } from './hot-resource/hot-resource.component'

interface PortfolioRes {
  list: Portfolio[]
  total: number
}

interface PortfolioListProps extends TableProps<any> {
  renderExtras?: any
  isReplaceRouter?: boolean
  extraOperationLoading?: boolean
}

export const PortfolioList: React.FC<PortfolioListProps> = ({
  renderExtras,
  isReplaceRouter = false,
  extraOperationLoading = false,
  ...extraTableProps
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const history = useHistory()
  const location = useLocation()
  const searchParsed = queryString.parse(location.search)
  const defaultParams = {}
  forIn(searchParsed, (value, key) => {
    switch (key) {
      case 'startDate':
        if (searchParsed['startDate'] && searchParsed['endDate']) {
          if (defaultParams['date']) {
            defaultParams['date'][0] = moment(value)
          } else {
            defaultParams['date'] = [moment(value), undefined]
          }
        }
        break
      case 'endDate':
        if (defaultParams['date']) {
          defaultParams['date'][1] = moment(value)
        } else {
          defaultParams['date'] = [undefined, moment(value)]
        }
        break
      case 'classification':
        if (value) {
          defaultParams['classification'] = Number(value)
        }
        break
      case 'page':
        defaultParams['current'] = Number(value)
        break
      case 'size':
        defaultParams['pageSize'] = Number(value)
        break
      case 'orderBy':
        defaultParams['order'] = value === 'asc' ? 'ascend' : value
        break
      case 'sortBy':
        defaultParams['columnKey'] = value
        break
      case 'priorities':
        if (isArray(value)) {
          defaultParams['priority'] = value.map((s) => Number(s))
        } else {
          defaultParams['priority'] = Number(value)
        }
        break
      default:
        defaultParams[key] = value
    }
  })

  const { tableProps, search } = useAntdTable<PortfolioRes, any, PortfolioRes>(
    ({ current, pageSize, sorter, filters }, params) => {
      isReplaceRouter &&
        history.replace({
          search: queryString.stringify({
            page: current,
            size: pageSize,
            name: params.name || null,
            classification: params.classification,
            startDate: params.date ? params.date[0].format('YYYY-MM-DD') : undefined,
            endDate: params.date ? params.date[1].format('YYYY-MM-DD') : undefined,
            email: params.email,
            ...(filters && filters.priority && { priorities: filters.priority }),
            sortBy: sorter?.columnKey,
            orderBy: sorter && sorter.order ? (sorter.order === 'ascend' ? 'asc' : 'desc') : undefined,
          }),
        })
      return {
        url: '/api/portfolio/getPortfolios',
        params: {
          page: current,
          size: pageSize,
          name: params.name || null,
          classification: params.classification,
          startDate: params.date ? params.date[0].format('YYYY-MM-DD') : undefined,
          endDate: params.date ? params.date[1].format('YYYY-MM-DD') : undefined,
          email: params.email,
          ...(filters && filters.priority && { priorities: filters.priority }),
          sortBy: sorter?.columnKey,
          orderBy: sorter && sorter.order ? (sorter.order === 'ascend' ? 'asc' : 'desc') : undefined,
        },
      }
    },
    {
      form,
      defaultParams: [
        {
          current: defaultParams['current'] || 1,
          pageSize: defaultParams['pageSize'] || 10,
          sorter: {
            order: defaultParams['order'] || undefined,
            columnKey: defaultParams['columnKey'] || undefined,
          },
          filters: {
            priority: defaultParams['priority'] || undefined,
            classification: defaultParams['classification'] || undefined,
            source: defaultParams['source'] || undefined,
          },
        },
        defaultParams,
      ],
      defaultPageSize: 10,
    }
  )

  const { run: runSearch } = useDebounceFn(search.submit, {
    wait: 500,
  })

  const columns: TableColumnsType<Portfolio> = [
    {
      title: t('portfolio.owner'),
      width: 150,
      ellipsis: true,
      render: (_, d) => {
        return (
          <div className='flex items-stretch'>
            <RiskBar risk={d.risk} />
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_负责人, d.permissions).canRead ? (
              <MosaicComponent />
            ) : (
              <UserAvatar showName email={d.email} />
            )}
          </div>
        )
      },
    },
    {
      title: t('portfolio.name'),
      width: 200,
      render: (_, d) => {
        return (
          <div className='flex items-center'>
            <div>
              <div>
                <Link
                  to={routes.portfolio.page(d.selfId)}
                  onClick={() => {
                    window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_portfolios)
                  }}
                >
                  <HotResource selfId={d.selfId} type={d.type} readonly hotData={d.hots} />
                  <span className='ml-4'>{d.name}</span>
                </Link>
              </div>
              <Typography.Text type='secondary'>
                {d.classification ? t(ProjectClassification[d.classification]) : null}
              </Typography.Text>
            </div>
          </div>
        )
      },
    },
    {
      title: t('devlop.detail.priority'),
      width: 100,
      align: 'center',
      filters: Object.keys(PriorityEnum)
        .filter((v) => isNaN(toNumber(v)))
        .map((key) => {
          const value = PriorityEnum[key]
          return {
            value,
            text: t(`form.priority.${key}`),
          }
        }),
      sorter: true,
      key: 'priority',
      filterMultiple: true,
      render: (_, d) =>
        !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, d.permissions).canRead ? (
          <MosaicComponent />
        ) : d.priority ? (
          t(`form.priority.${PriorityEnum[d.priority]}`)
        ) : null,
    },
    {
      title: t('progress'),
      width: 80,
      render: (_, d) => {
        return getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_进度, d.permissions).canRead ? (
          <Progress className='font-mono flex-shrink-0' type='circle' width={26} percent={round(d.progress, 0)} />
        ) : (
          <MosaicComponent />
        )
      },
    },
    {
      title: t('startend'),
      width: 200,
      align: 'center',
      sorter: true,
      key: 'startDate',
      render: (_, d) => {
        return (
          <div className='flex text-sm'>
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, d.permissions).canRead ? (
              <MosaicComponent />
            ) : (
              <>
                <div className='w-80px'>{d.startDate && moment(d.startDate).format('l')}</div>
                <div className='px-4px'>-</div>
                <div className='w-80px'>{d.endDate && moment(d.endDate).format('l')}</div>
              </>
            )}
          </div>
        )
      },
    },
  ]

  if (renderExtras) {
    columns.push({
      width: 80,
      align: 'center',
      render: renderExtras,
    })
  }

  return (
    <>
      <Form form={form} onValuesChange={runSearch}>
        <Row gutter={[24, 0]}>
          <Col xs={6} lg={4}>
            <Form.Item name='name'>
              <Input allowClear placeholder={t('portfolio.name')} />
            </Form.Item>
          </Col>
          <Col xs={6} lg={4}>
            <Form.Item name='classification'>
              <Select allowClear placeholder={t('portfolio.classification')} options={classificationOptions}></Select>
            </Form.Item>
          </Col>
          <Col xs={6} lg={4}>
            <Form.Item name='email'>
              <UsersSearch placeholder={t('owner')} />
            </Form.Item>
          </Col>
          <Col xs={12} lg={8}>
            <Form.Item name='date'>
              <DatePicker.RangePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table
        rowKey='selfId'
        size='middle'
        columns={columns}
        {...tableProps}
        {...extraTableProps}
        loading={extraOperationLoading || tableProps.loading}
        // @ts-ignore
        pagination={{
          ...tableProps.pagination,
          showTotal: (total) => `${total} 个组合`,
          hideOnSinglePage: true,
        }}
      />
    </>
  )
}
